import moment from "moment";
import { useEffect, useState } from "react";
import { FaSpinner } from "react-icons/fa";
import { GoMute } from "react-icons/go";
import { MdSearch } from "react-icons/md";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Api from "../../service/http";

const ChatList = ({
  chats,
  typingChat = [],
  type = "GR",
  subscribed = true,
  onSubscribeRequired,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const activeChatId = location.pathname.split("/").filter(Boolean)[1];
  const { theme } = useSelector((state) => state.theme);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);

  const handleChatClick = async (chatItem) => {
    if (!subscribed) {
      onSubscribeRequired?.();
      return;
    }
    const isPrUserSearch =
      type === "PR" && debouncedSearch.trim() && chatItem?._userSearch;
    if (isPrUserSearch) {
      try {
        const res = await Api.post("/app/chats", {
          users: [chatItem.id],
          chat_type: "PR",
        });
        const chatId = res?.data?.id;
        if (chatId) navigate(`/personal-chat/${chatId}`);
      } catch {
        /* keep user on list */
      }
      return;
    }
    navigate(
      type === "PR"
        ? `/personal-chat/${chatItem?.id}`
        : `/chat/${chatItem?.id}`,
    );
  };

  const [filteredChats, setFilteredChats] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (!q) {
      setFilteredChats(chats ?? []);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    const path =
      type === "PR"
        ? `/app/search/users?search=${encodeURIComponent(q)}`
        : `/app/search/chats?search=${encodeURIComponent(q)}`;

    Api.get(path)
      .then((res) => {
        if (cancelled) return;
        const raw = Array.isArray(res.data) ? res.data : [];
        const list =
          type === "PR" ? raw.map((u) => ({ ...u, _userSearch: true })) : raw;
        setFilteredChats(list);
      })
      .catch(() => {
        if (!cancelled) setFilteredChats([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, chats, type]);

  return (
    <div
      className={`flex flex-col h-full w-full ${theme === "dark" ? "bg-[#232634]" : "bg-white"}`}
    >
      {/* Search and Profile */}
      <div className="p-4 border-b flex items-center gap-3">
        <div className="relative flex-1">
          <MdSearch
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
            size={20}
          />
          <input
            type="text"
            placeholder="Search..."
            className={`w-full pl-10 pr-4 py-2 rounded-full text-sm outline-none transition-all ${
              theme === "dark"
                ? "bg-gray-800 text-gray-200 border-gray-700 focus:border-indigo-500"
                : "bg-gray-100 text-gray-800 border-transparent focus:bg-white focus:border-indigo-500 border"
            }`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <div className="p-2">
          {searchLoading ? (
            <div className="flex justify-center py-8 text-gray-500">
              <FaSpinner className="animate-spin" size={22} />
            </div>
          ) : filteredChats?.length > 0 ? (
            filteredChats.map((chatItem, i) => {
              const rowChatId = chatItem?._userSearch ? null : chatItem?.id;
              const isTyping =
                !chatItem?._userSearch &&
                typingChat?.map((itm) => itm?.id)?.includes(chatItem?.id);
              const isActive =
                rowChatId != null && String(rowChatId) === activeChatId;

              return (
                <div
                  key={chatItem?.id ?? i}
                  className={`flex items-center gap-3 p-3 mb-1 rounded-lg cursor-pointer transition-all duration-200 ${
                    isActive
                      ? "bg-indigo-500 text-white shadow-md"
                      : theme === "dark"
                        ? "hover:bg-gray-800 text-gray-300"
                        : "hover:bg-gray-50 text-gray-700"
                  }`}
                  onClick={() => handleChatClick(chatItem)}
                >
                  <div className="relative shrink-0">
                    <img
                      src={chatItem?.image || "https://placehold.co/400"}
                      className="w-11 h-11 rounded-full object-cover shadow-sm"
                      alt="user"
                    />
                    {/* Status badge - if applicable, otherwise showing a placeholder dot */}
                    {/* <div
                      className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 ${
                        theme === "dark" ? "border-gray-900" : "border-white"
                      } bg-green-500`}
                    ></div> */}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <p
                        className={`text-sm font-medium truncate ${isActive ? "text-white" : ""}`}
                      >
                        {chatItem?.name}
                      </p>
                      <span
                        className={`text-[11px] shrink-0 ${isActive ? "text-indigo-100" : "text-gray-400"}`}
                      >
                        {chatItem?.last_msg?.created_at
                          ? moment(chatItem.last_msg.created_at).format(
                              "hh:mm A",
                            )
                          : ""}
                      </span>
                    </div>

                    <div className="flex justify-between items-center">
                      <p
                        className={`text-xs truncate ${
                          isActive
                            ? "text-indigo-100"
                            : isTyping
                              ? "text-green-500 font-medium"
                              : "text-gray-500"
                        }`}
                      >
                        {isTyping
                          ? "Typing..."
                          : chatItem?.last_msg?.message
                            ? chatItem?.last_msg?.message
                            : debouncedSearch
                              ? ""
                              : "No messages yet"}
                      </p>

                      {chatItem?.unread_count > 0 && !isActive && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                          {chatItem?.unread_count}
                        </span>
                      )}
                      {chatItem?.mute && !isActive && (
                        <GoMute className="ml-2 text-gray-400" size={14} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-4 text-center text-gray-500 text-sm">
              {debouncedSearch.trim()
                ? "No chats found"
                : type === "PR"
                  ? "No conversations yet. Search for a user to start one."
                  : "No group chats yet."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatList;
